/**
 * Página de Plano de Contas - Design Minimalista
 * Cadastro simples de naturezas de despesas (dedutíveis e não dedutíveis)
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  SmartToy as SmartToyIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import planoContasService from '../../services/planoContasService';
import chatService from '../../services/chatService';

const PlanoContas = () => {
  const { isDarkMode } = useAppTheme();
  const [contas, setContas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0); // 0 = Dedutíveis, 1 = Não Dedutíveis

  // Form states
  const [nome, setNome] = useState('');
  const [dedutivel, setDedutivel] = useState(true);
  const [editando, setEditando] = useState(null);

  // Modal IA
  const [openModalIA, setOpenModalIA] = useState(false);
  const [perguntaIA, setPerguntaIA] = useState('');
  const [respostaIA, setRespostaIA] = useState('');
  const [respostaIATyped, setRespostaIATyped] = useState('');
  const typingIntervalRef = useRef(null);
  const [loadingIA, setLoadingIA] = useState(false);

  useEffect(() => {
    carregarContas();
  }, []);

  const carregarContas = async () => {
    try {
      setLoading(true);
      console.log('🔵 Carregando plano de contas...');
      const data = await planoContasService.listar('despesa'); // Só despesas
      console.log('✅ Contas recebidas:', data);
      setContas(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      setError('Erro ao carregar contas');
      console.error('❌ Erro:', err);
      setContas([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdicionar = async () => {
    if (!nome.trim()) {
      setError('Digite o nome da natureza');
      return;
    }

    try {
      setLoading(true);
      const dados = {
        codigo: Date.now().toString(), // Código automático
        nome: nome.trim(),
        tipo: 'despesa',
        dedutivel: dedutivel,
        categoria: dedutivel ? 'Dedutível' : 'Não Dedutível',
        ativo: true
      };

      if (editando) {
        await planoContasService.atualizar(editando.id, dados);
        setSuccess('Natureza atualizada!');
      } else {
        await planoContasService.criar(dados);
        setSuccess('Natureza adicionada!');
      }

      // Limpar form
      setNome('');
      setEditando(null);
      setDedutivel(true);

      carregarContas();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (conta) => {
    setEditando(conta);
    setNome(conta.nome);
    setDedutivel(conta.dedutivel);
    // Muda para aba correspondente
    setTabValue(conta.dedutivel ? 0 : 1);
  };

  const handleCancelarEdicao = () => {
    setEditando(null);
    setNome('');
    setDedutivel(true);
  };

  const handleDeletar = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta natureza?')) return;

    try {
      await planoContasService.deletar(id);
      setSuccess('Natureza excluída!');
      carregarContas();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao excluir');
    }
  };

  const handleConsultarIA = async () => {
    if (!perguntaIA.trim()) {
      setError('Digite sua dúvida');
      return;
    }

    try {
      setLoadingIA(true);
      setRespostaIA('');
      setRespostaIATyped('');
      setError('');

      console.log('🤖 Consultando IA sobre:', perguntaIA);

      // Chama a API real da OpenAI usando o chatService
      const data = await chatService.consultarDedutibilidade(perguntaIA);

      console.log('✅ Resposta da IA recebida');
      setRespostaIA(data.resposta);

      // Efeito de digitação
      let i = 0;
      typingIntervalRef.current && clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = setInterval(() => {
        setRespostaIATyped((prev) => {
          if (i < data.resposta.length) {
            i++;
            return data.resposta.slice(0, i);
          } else {
            clearInterval(typingIntervalRef.current);
            return data.resposta;
          }
        });
      }, 18); // velocidade da digitação

    } catch (err) {
      console.error('❌ Erro ao consultar IA:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao consultar IA. Verifique sua conexão.';
      setError(errorMessage);
    } finally {
      setLoadingIA(false);
    }
  };

  const contasDedutiveis = (contas || []).filter(c => c.dedutivel);
  const contasNaoDedutiveis = (contas || []).filter(c => !c.dedutivel);
  const contasExibidas = tabValue === 0 ? contasDedutiveis : contasNaoDedutiveis;

  // Limpa o intervalo ao fechar o modal
  useEffect(() => {
    if (!openModalIA) {
      typingIntervalRef.current && clearInterval(typingIntervalRef.current);
      setRespostaIATyped('');
    }
  }, [openModalIA]);

  return (
    <Box sx={{ backgroundColor: isDarkMode ? undefined : '#FFFAF5', minHeight: '100%', p: 0 }}>
      {/* Cabeçalho */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ borderLeft: '5px solid #F97316', pl: 1.5 }}>
          Plano de Contas
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5 }}>
          Cadastre as naturezas de despesas para classificação fiscal
        </Typography>
      </Box>

      {/* Alertas */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Formulário Inline */}
      <Paper sx={{ p: 3, mb: 3, borderTop: '3px solid #F97316', background: isDarkMode ? undefined : '#FFF7ED' }}>
        <Typography variant="h6" gutterBottom fontWeight={600} sx={{ borderLeft: '3px solid #F97316', pl: 1.5 }}>
          {editando ? 'Editar Natureza' : 'Nova Natureza'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            label="Nome da Natureza"
            placeholder="Ex: Água, Aluguel, Marketing"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            sx={{ flex: 1, minWidth: { xs: '100%', sm: 300 } }}
            size="small"
            onKeyPress={(e) => e.key === 'Enter' && handleAdicionar()}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={dedutivel}
                onChange={(e) => setDedutivel(e.target.checked)}
                color="success"
              />
            }
            label="Dedutível"
            sx={{ mt: 0.5 }}
          />

          <Button
            variant="contained"
            startIcon={editando ? <EditIcon /> : <AddIcon />}
            onClick={handleAdicionar}
            disabled={loading || !nome.trim()}
            sx={{
              bgcolor: '#F97316', fontWeight: 700,
              boxShadow: '0 2px 10px rgba(249,115,22,0.35)',
              '&:hover': { bgcolor: '#EA580C' },
              '&:disabled': { bgcolor: 'rgba(249,115,22,0.3)' },
            }}
          >
            {editando ? 'Atualizar' : 'Adicionar'}
          </Button>

          {editando && (
            <Button variant="outlined" onClick={handleCancelarEdicao}
              sx={{ borderColor: '#F97316', color: '#F97316', '&:hover': { borderColor: '#EA580C', bgcolor: 'rgba(249,115,22,0.06)' } }}
            >
              Cancelar
            </Button>
          )}

          <Button
            variant="outlined"
            startIcon={<SmartToyIcon />}
            onClick={() => setOpenModalIA(true)}
            sx={{
              borderColor: '#F97316', color: '#F97316', fontWeight: 600,
              '&:hover': { borderColor: '#EA580C', bgcolor: 'rgba(249,115,22,0.06)', color: '#EA580C' },
            }}
          >
            Consultar IA
          </Button>
        </Box>
      </Paper>

      {/* Abas Dedutíveis / Não Dedutíveis */}
      <Paper sx={{ borderTop: '2px solid rgba(249,115,22,0.25)' }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{
            borderBottom: '1px solid rgba(249,115,22,0.18)',
            '& .MuiTabs-indicator': { backgroundColor: '#F97316', height: 3 },
          }}
        >
          <Tab
            label={`Dedutíveis (${contasDedutiveis.length})`}
            sx={{ fontWeight: 600, '&.Mui-selected': { color: '#F97316' } }}
          />
          <Tab
            label={`Não Dedutíveis (${contasNaoDedutiveis.length})`}
            sx={{ fontWeight: 600, '&.Mui-selected': { color: '#F97316' } }}
          />
        </Tabs>

        {/* Tabela */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ background: isDarkMode ? 'rgba(249,115,22,0.07)' : 'rgba(249,115,22,0.05)' }}>
                <TableCell sx={{ fontWeight: 700, color: '#F97316', fontSize: '0.78em', letterSpacing: '0.8px', textTransform: 'uppercase' }}>Nome</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#F97316', fontSize: '0.78em', letterSpacing: '0.8px', textTransform: 'uppercase' }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : contasExibidas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <Typography color="text.secondary">
                      Nenhuma natureza cadastrada
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                contasExibidas.map((conta) => (
                  <TableRow key={conta.id} sx={{ '&:hover': { bgcolor: 'rgba(249,115,22,0.04)' }, borderBottom: '1px solid rgba(249,115,22,0.1)' }}>
                    <TableCell>
                      <Typography variant="body2">{conta.nome}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEditar(conta)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeletar(conta.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Modal Consultar IA */}
      <Dialog
        open={openModalIA}
        onClose={() => {
          setOpenModalIA(false);
          setPerguntaIA('');
          setRespostaIA('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: isDarkMode ? {
            background: 'rgba(30, 41, 59, 0.92)',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
            backdropFilter: 'blur(8px)',
            borderRadius: 4,
            border: '1px solid rgba(255,255,255,0.12)',
          } : {
            background: '#FFFAF5',
            boxShadow: '0 8px 32px rgba(249,115,22,0.18)',
            borderRadius: 4,
            border: '2px solid rgba(249,115,22,0.3)',
          }
        }}
      >
        <DialogTitle sx={{ background: 'transparent', pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: isDarkMode
                ? 'linear-gradient(135deg, #00c3ff 0%, #fe9800ff 100%)'
                : 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: isDarkMode ? '0 0 16px #00c3ff88' : '0 0 16px rgba(249,115,22,0.5)',
              animation: 'pulse 1.5s infinite',
            }}>
              <SmartToyIcon sx={{ color: '#232526', fontSize: 32 }} />
            </Box>
            <Typography variant="h6" sx={{ color: isDarkMode ? '#fff' : '#1C1917', fontWeight: 700, letterSpacing: 1 }}>
              Aline
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ background: 'transparent', color: isDarkMode ? '#fff' : 'text.primary', borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(249,115,22,0.2)' }}>
          <TextField
            fullWidth
            label="Qual despesa você tem dúvida se é dedutível?"
            placeholder="Ex: Conta de água do consultório"
            value={perguntaIA}
            onChange={(e) => setPerguntaIA(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && e.ctrlKey && perguntaIA.trim()) {
                handleConsultarIA();
              }
            }}
          />

          {loadingIA && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Consultando assistente fiscal...
              </Typography>
            </Box>
          )}

          {respostaIATyped && !loadingIA && (
            <Paper sx={{
              p: 2,
              background: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(249,115,22,0.06)',
              color: isDarkMode ? '#ffffff' : '#1C1917',
              borderRadius: 2,
              boxShadow: isDarkMode ? '0 2px 8px rgba(0,195,255,0.12)' : '0 2px 8px rgba(249,115,22,0.12)',
              border: isDarkMode ? '1px solid rgba(0,195,255,0.18)' : '1px solid rgba(249,115,22,0.25)',
              fontFamily: 'Fira Mono, monospace',
              fontSize: 16,
              position: 'relative',
              minHeight: 120,
            }}>
              <ReactMarkdown
                components={{
                  p: ({ node, ...props }) => <Typography variant="body2" sx={{ mb: 1, fontFamily: 'inherit' }} {...props} />,
                  li: ({ node, ...props }) => <li style={{ marginBottom: 4, fontFamily: 'inherit' }} {...props} />,
                  strong: ({ node, ...props }) => <strong style={{ fontWeight: 700, fontFamily: 'inherit' }} {...props} />,
                }}
              >
                {respostaIATyped}
              </ReactMarkdown>
              {/* cursor piscando */}
              {respostaIATyped.length < respostaIA.length && (
                <span style={{
                  display: 'inline-block',
                  width: 10,
                  height: 24,
                  background: 'rgba(0,195,255,0.7)',
                  marginLeft: 2,
                  borderRadius: 2,
                  animation: 'blink 1s steps(2, start) infinite',
                  verticalAlign: 'middle',
                }} />
              )}
              <style>{`
                @keyframes blink {
                  0%, 100% { opacity: 1; }
                  50% { opacity: 0; }
                }
                @keyframes pulse {
                  0% { box-shadow: 0 0 16px rgba(249,115,22,0.5); }
                  50% { box-shadow: 0 0 32px rgba(249,115,22,0.8); }
                  100% { box-shadow: 0 0 16px rgba(249,115,22,0.5); }
                }
              `}</style>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: (theme) => theme.palette.background.paper }}>
          <Button
            onClick={() => {
              setOpenModalIA(false);
              setPerguntaIA('');
              setRespostaIA('');
            }}
          >
            Fechar
          </Button>
          <Button
            variant="contained"
            onClick={handleConsultarIA}
            disabled={loadingIA || !perguntaIA.trim()}
            sx={{
              bgcolor: '#F97316', fontWeight: 700,
              '&:hover': { bgcolor: '#EA580C' },
              '&:disabled': { bgcolor: 'rgba(249,115,22,0.3)' },
            }}
          >
            {loadingIA ? 'Consultando...' : 'Consultar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlanoContas;

